import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHero } from '../components/ui/PageHero';
import { AnimatedCard } from '../components/ui/AnimatedCard';
import { CTAButton } from '../components/ui/CTAButton';
import { RelatedLinks } from '../components/ui/RelatedLinks';
import { getProduct, PRODUCTS, CONTACT_EMAIL } from '../constants/content';
import { inquiryPath, productPath } from '../constants/navigation';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = getProduct(slug);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const otherProducts = PRODUCTS.filter((p) => p.slug !== slug);

  return (
    <>
      <PageHero
        title={product.title}
        subtitle={product.shortDesc}
        breadcrumb={[
          { label: '首页', path: '/' },
          { label: '产品设备', path: '/products' },
          { label: product.title },
        ]}
      />

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto mb-16">
          <AnimatedCard className="bg-darker/50 rounded-3xl border border-purple-500/20 p-8 flex items-center justify-center aspect-square">
            <img
              src={product.image}
              alt={product.imageAlt}
              className="max-h-full max-w-full object-contain drop-shadow-2xl"
            />
          </AnimatedCard>

          <div>
            <span
              className={`inline-block text-xs font-semibold uppercase tracking-wider ${product.badgeClass} px-3 py-1 rounded-full mb-4`}
            >
              {product.badge}
            </span>
            <p className="text-purple-400 text-sm mb-2">{product.priceNote}</p>
            <p className="text-purple-300 text-xs mb-6">起订：{product.moq}</p>
            <p className="text-purple-200 leading-relaxed mb-8">{product.description}</p>

            <h2 className="text-sm font-semibold text-secondary mb-3">产品亮点</h2>
            <ul className="space-y-2 text-purple-200 text-sm mb-8">
              {product.highlights.map((text) => (
                <li key={text} className="flex items-start">
                  <i className="fa fa-leaf text-green-400 mt-0.5 mr-2" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <CTAButton to={inquiryPath(product.slug)} variant="primary" icon="fa-shopping-cart">
                购买咨询
              </CTAButton>
              <CTAButton to="/inquiry" variant="secondary">
                全部产品咨询
              </CTAButton>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div>
            <h2 className="text-2xl font-bold mb-6">产品规格</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {product.specs.map((spec) => (
                <div
                  key={spec.label}
                  className="bg-white/5 rounded-xl p-4 border border-purple-500/20"
                >
                  <dt className="text-purple-400 text-sm mb-1">{spec.label}</dt>
                  <dd className="text-light">{spec.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-6">包装选项</h2>
            <ul className="space-y-3 mb-8">
              {product.packaging.map((pkg) => (
                <li
                  key={pkg}
                  className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-purple-500/20"
                >
                  <i className="fa fa-gift text-secondary" />
                  <span className="text-purple-200 text-sm">{pkg}</span>
                </li>
              ))}
            </ul>
            <h2 className="text-2xl font-bold mb-6">适用场景</h2>
            <ul className="space-y-2">
              {product.scenarios.map((s) => (
                <li key={s} className="text-purple-300 text-sm flex items-start gap-2">
                  <i className="fa fa-check text-green-400 mt-1 text-xs" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold mb-6">保养说明</h2>
          <ul className="space-y-3">
            {product.care.map((text) => (
              <li
                key={text}
                className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-purple-500/20"
              >
                <i className="fa fa-info-circle text-secondary mt-0.5" />
                <span className="text-purple-200 text-sm">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {otherProducts.length > 0 && (
          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-2xl font-bold mb-6">同系列其他产品</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherProducts.map((p) => (
                <div
                  key={p.slug}
                  className="link-card bg-white/5 rounded-2xl p-4 border border-purple-500/20"
                >
                  <Link to={productPath(p.slug)} className="flex items-center gap-4 mb-2">
                    <img src={p.image} alt={p.title} className="w-16 h-16 object-contain" />
                    <div>
                      <p className="font-semibold">{p.title}</p>
                      <p className="text-purple-300 text-sm">{p.shortDesc}</p>
                    </div>
                  </Link>
                  <Link
                    to={inquiryPath(p.slug)}
                    className="text-secondary text-xs hover:underline ml-20"
                  >
                    购买咨询 →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <RelatedLinks
            links={[
              { to: inquiryPath(product.slug), label: '提交购买咨询' },
              { to: '/contact/gift', label: '礼品批量采购' },
              { to: '/products', label: '返回产品列表' },
              { to: '/inquiry', label: '购买咨询首页' },
            ]}
          />
        </div>

        <p className="text-center text-purple-400 text-sm mt-12">
          采购咨询邮箱：
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-secondary hover:underline ml-1">
            {CONTACT_EMAIL}
          </a>
        </p>
      </section>
    </>
  );
}
